/**
 * SprintDraftBlock — one block per draft strategy sprint.
 *
 * Renders:
 *   - Sprint header (name, dates, primary audience dropdown)
 *   - audienceRationale textarea (autosave on blur)
 *   - Theme quota table (read-only — quota edits are an advanced operation)
 *   - Goal quota table (read-only mirror)
 *   - sprint.audienceCoverageViolations[] badges
 *   - per-(audience, theme) gap chips with "Generate N topics" buttons
 *   - dataUsage chips (clickable later — opens cited brief)
 *
 * Persistence pattern: scalar fields use dot-notation
 *   `sprints.{sprintId}.primaryAudienceBriefRef = ...`
 *   `sprints.{sprintId}.audienceRationale = ...`
 * Whole-array replacements (outputThemeQuotas / outputGoalQuotas) are
 * staged but NOT exposed here — they're advanced and deferred to a future
 * inline editor.
 */

import React, { useMemo, useState } from 'react';
import {
  Paper,
  Stack,
  Typography,
  Box,
  Chip,
  TextField,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import SyncProblemIcon from '@mui/icons-material/SyncProblem';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import type {
  ContentSprint,
  ContentTopic,
  ContentTheme,
  AudienceBriefRef,
} from '@modules/content-plan/types';
import {
  checkSprintAudienceSufficiency,
  type SprintAudienceSufficiency,
} from '@modules/content-plan/utils/sprintAudienceSufficiency';
import { resolveThemeId } from '@modules/content-plan/utils/themeKeys';

const formatDate = (v: unknown): string => {
  if (!v) return '';
  try {
    if (typeof v === 'string') return v.slice(0, 10);
    if (v instanceof Date) return v.toISOString().slice(0, 10);
    // Firestore Timestamp
    const ts = v as { toDate?: () => Date; seconds?: number };
    if (typeof ts.toDate === 'function') return ts.toDate().toISOString().slice(0, 10);
    if (typeof ts.seconds === 'number') return new Date(ts.seconds * 1000).toISOString().slice(0, 10);
  } catch {
    /* fall through */
  }
  return '';
};

const refKey = (r: AudienceBriefRef | undefined): string =>
  r ? `${r.analysisId}::${r.audienceId}` : '';

const formatCoverageCount = (value: number): string =>
  Number.isInteger(value) ? String(value) : value.toFixed(1);

export interface SprintDraftBlockProps {
  sprint: ContentSprint;
  index: number;
  /** Selected refs from strategy.primaryAudienceBriefRefs — the dropdown's option set. */
  availableAudienceBriefRefs: AudienceBriefRef[];
  /** Backlog used for per-(audience, theme) sufficiency check. */
  backlog: ContentTopic[];
  /** Theme catalog — required by resolveAudienceFits's inheritance fallback. */
  themeCatalog: Record<string, ContentTheme>;
  /** Per-sprint scalar field patch. */
  onPatchScalar: (path: string, value: unknown) => Promise<void>;
  /** Per-sprint gap-fill trigger — dispatches TopicBacklogOrchestrator with audience pinned. */
  onGenerateTopics: (sprintId: string, themeIds: string[], count?: number) => Promise<void>;
  disabled?: boolean;
}

export const SprintDraftBlock: React.FC<SprintDraftBlockProps> = ({
  sprint,
  index,
  availableAudienceBriefRefs,
  backlog,
  themeCatalog,
  onPatchScalar,
  onGenerateTopics,
  disabled = false,
}) => {
  const primaryAudienceBriefRef = (sprint as any).primaryAudienceBriefRef as AudienceBriefRef | undefined;
  const audienceRationale = (sprint as any).audienceRationale as string | undefined;
  const audienceCoverageViolations = ((sprint as any).audienceCoverageViolations as
    | Array<{
        expectedAudienceId?: string;
        actualAudienceIds?: string[];
        offendingTopicIds?: string[];
        offendingAssignmentKeys?: string[];
        expectedCount?: number;
        actualCount?: number;
        severity?: 'under' | 'missing';
      }>
    | undefined) ?? [];

  const themeQuotas = (((sprint as any).sprintContract?.outputThemeQuotas as
    | Array<{ themeId?: string; theme?: string; requiredCount: number; tolerance?: number }>
    | undefined) ?? []);
  const goalQuotas = (((sprint as any).sprintContract?.outputGoalQuotas as
    | Array<{ goal: string; requiredCount: number; tolerance?: number }>
    | undefined) ?? []);
  const sprintDataUsageItems = ((sprint as any).sprintDataUsage?.items as
    | Array<{ sourcePath?: string; justification?: string; value?: string }>
    | undefined) ?? [];

  const [rationaleDraft, setRationaleDraft] = useState(audienceRationale ?? '');
  const [savingField, setSavingField] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [generatingTheme, setGeneratingTheme] = useState<string | null>(null);

  React.useEffect(() => {
    // Mirror upstream changes when not focused
    setRationaleDraft(audienceRationale ?? '');
  }, [audienceRationale]);

  // Compute this sprint's gaps using the shared sufficiency util — we feed it
  // a single-sprint slice so the answer is just one element.
  const sufficiency: SprintAudienceSufficiency | null = useMemo(() => {
    const result = checkSprintAudienceSufficiency({
      sprints: [sprint],
      backlog,
      selectedAudienceBriefRefs: availableAudienceBriefRefs,
      themeCatalog,
    });
    return result.perSprint[0] ?? null;
  }, [sprint, backlog, availableAudienceBriefRefs, themeCatalog]);

  const handleChangeAudience = async (newKey: string) => {
    if (disabled) return;
    setError(null);
    setSavingField('primaryAudienceBriefRef');
    try {
      const ref = availableAudienceBriefRefs.find((r) => refKey(r) === newKey);
      // Null clears the field — universal sprint.
      await onPatchScalar(`sprints.${sprint.id}.primaryAudienceBriefRef`, ref ?? null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSavingField(null);
    }
  };

  const handleSaveRationale = async () => {
    if (disabled) return;
    if ((rationaleDraft ?? '').trim() === (audienceRationale ?? '').trim()) return;
    setError(null);
    setSavingField('audienceRationale');
    try {
      await onPatchScalar(`sprints.${sprint.id}.audienceRationale`, rationaleDraft.trim());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSavingField(null);
    }
  };

  const handleGenerateForTheme = async (themeId: string) => {
    if (disabled) return;
    setError(null);
    setGeneratingTheme(themeId);
    try {
      const gap = sufficiency?.themeGaps.find((g) => g.themeId === themeId)?.gap;
      await onGenerateTopics(sprint.id, [themeId], gap && gap > 0 ? gap : undefined);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setGeneratingTheme(null);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid={`sprint-draft-block-${sprint.id}`}>
      {/* Header */}
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <Chip size="small" label={`Sprint ${index + 1}`} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {(sprint as any).name || sprint.id}
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {formatDate((sprint as any).dateStart)} → {formatDate((sprint as any).dateEnd)}
            {(sprint as any).sprintType && ` · ${(sprint as any).sprintType}`}
          </Typography>
        </Box>

        {/* Audience dropdown */}
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <Select
            value={refKey(primaryAudienceBriefRef)}
            displayEmpty
            disabled={disabled || savingField === 'primaryAudienceBriefRef'}
            onChange={(e) => handleChangeAudience(e.target.value)}
            inputProps={{ 'data-testid': `sprint-audience-select-${sprint.id}` }}
          >
            <MenuItem value="">
              <em>Universal sprint (no primary audience)</em>
            </MenuItem>
            {availableAudienceBriefRefs.map((r) => (
              <MenuItem key={refKey(r)} value={refKey(r)}>
                {r.labelSnapshot ?? r.audienceId}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {/* Audience rationale */}
      <Box sx={{ mt: 1.5 }}>
        <TextField
          label="Audience rationale"
          fullWidth
          multiline
          minRows={2}
          value={rationaleDraft}
          onChange={(e) => setRationaleDraft(e.target.value)}
          onBlur={handleSaveRationale}
          disabled={disabled || savingField === 'audienceRationale'}
          helperText="1-2 sentences anchored to readinessStage + forceBalance."
          inputProps={{ 'data-testid': `sprint-audience-rationale-${sprint.id}` }}
        />
      </Box>

      {/* Coverage violations (from validator on apply) */}
      {audienceCoverageViolations.length > 0 && (
        <Alert
          severity={audienceCoverageViolations.some((v) => v.severity === 'missing') ? 'error' : 'warning'}
          icon={<SyncProblemIcon />}
          sx={{ mt: 1.5 }}
          data-testid={`sprint-coverage-violations-${sprint.id}`}
        >
          <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
            Audience coverage violations ({audienceCoverageViolations.length})
          </Typography>
          <Stack spacing={0.5} sx={{ mt: 0.5 }}>
            {audienceCoverageViolations.slice(0, 5).map((v, i) => (
              <Typography key={i} variant="caption" sx={{ display: 'block' }}>
                {`${v.severity ?? 'under'}: expected audience ${v.expectedAudienceId ?? '(none)'} got ${(v.actualAudienceIds ?? []).join(', ') || '(none)'} · ${v.actualCount ?? 0}/${v.expectedCount ?? 0}`}
              </Typography>
            ))}
            {audienceCoverageViolations.length > 5 && (
              <Typography variant="caption" color="text.secondary">
                +{audienceCoverageViolations.length - 5} more…
              </Typography>
            )}
          </Stack>
        </Alert>
      )}

      {/* Theme quota + per-theme gap chips */}
      {themeQuotas.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="subtitle2" gutterBottom>Content mix targets</Typography>
          <Stack spacing={0.75} data-testid={`sprint-theme-quotas-${sprint.id}`}>
            {themeQuotas.map((q, i) => {
              const themeId = resolveThemeId(q);
              const gapEntry = sufficiency?.themeGaps.find((g) => g.themeId === themeId);
              const hasGap = (gapEntry?.gap ?? 0) > 0;
              const themeName = themeCatalog[themeId]?.name ?? (themeId || 'Unscoped theme quota');
              return (
                <Stack
                  key={`${themeId || 'unscoped'}-${i}`}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ p: 0.5, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
                >
                  <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                    {themeName}
                  </Typography>
                  <Chip size="small" label={`needs ${q.requiredCount}`} variant="outlined" />
                  {gapEntry && (
                    <Chip
                      size="small"
                      color={hasGap ? 'warning' : 'success'}
                      variant={hasGap ? 'filled' : 'outlined'}
                      label={`have ${formatCoverageCount(gapEntry.have)}`}
                    />
                  )}
                  {hasGap && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={generatingTheme === themeId ? <CircularProgress size={14} /> : <AutoFixHighIcon />}
                      onClick={() => handleGenerateForTheme(themeId)}
                      disabled={disabled || generatingTheme !== null || !themeId}
                      data-testid={`sprint-gap-generate-${sprint.id}-${themeId || 'unscoped'}`}
                    >
                      Generate {gapEntry?.gap ?? 1}
                    </Button>
                  )}
                </Stack>
              );
            })}
          </Stack>
        </Box>
      )}

      {/* Goal quota (read-only) */}
      {goalQuotas.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="subtitle2" gutterBottom>Goal quotas</Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {goalQuotas.map((g, i) => (
              <Chip
                key={`${g.goal}-${i}`}
                size="small"
                variant="outlined"
                label={`${g.goal}: ${g.requiredCount}`}
              />
            ))}
          </Stack>
        </Box>
      )}

      {/* dataUsage chips */}
      {sprintDataUsageItems.length > 0 && (
        <Box sx={{ mt: 1.5 }} data-testid={`sprint-data-usage-${sprint.id}`}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Data citations ({sprintDataUsageItems.length})
          </Typography>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {sprintDataUsageItems.slice(0, 8).map((item, i) => (
              <Chip
                key={i}
                size="small"
                variant="outlined"
                label={item.sourcePath ?? `citation ${i + 1}`}
                title={item.justification ?? item.value ?? ''}
              />
            ))}
            {sprintDataUsageItems.length > 8 && (
              <Chip size="small" label={`+${sprintDataUsageItems.length - 8} more`} />
            )}
          </Stack>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {error}
        </Alert>
      )}
    </Paper>
  );
};

export default SprintDraftBlock;
